import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/lang";
import { LANGS, SIGN_IN_URL } from "@/content";
import { Logo } from "./Logo";
import { goTo, ease } from "./motion";

const LINKS = ["property", "gallery", "plans", "company", "club", "contact"];

export const LangSwitch = ({ testPrefix = "nav", dark = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center rounded-full border p-0.5 transition-colors duration-500 ${dark ? "border-white/30 bg-white/10" : "border-[var(--line)] bg-white/50"}`} dir="ltr">
      {LANGS.map((l) => (
        <button
          key={l.code}
          data-testid={`${testPrefix}-lang-${l.code}`}
          onClick={() => setLang(l.code)}
          aria-label={l.name}
          className={`px-2.5 py-1 text-[12px] rounded-full transition-colors duration-300 ${lang === l.code ? "bg-[var(--brass-deep)] text-white" : dark ? "text-white/80 hover:text-white" : "text-[var(--ink-soft)] hover:text-[var(--ink)]"}`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

export const Nav = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setOpen(false); goTo(id); };
  const dark = !scrolled;

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease, delay: 0.9 }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${scrolled ? "bg-white/75 backdrop-blur-xl shadow-[0_1px_0_var(--line)]" : ""}`}
        data-testid="site-nav"
      >
        <div className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between px-5 md:px-10">
          <button onClick={() => go("top")} data-testid="nav-logo-btn" aria-label="OGEN Hospitality"><Logo light={dark} /></button>
          <nav className="hidden xl:flex items-center gap-7">
            {LINKS.map((k) => (
              <button key={k} data-testid={`nav-link-${k}`} onClick={() => go(k)} className={`link-line text-[13px] transition-colors duration-500 ${dark ? "text-white/80 hover:text-white" : "text-[var(--ink-soft)] hover:text-[var(--ink)]"}`}>
                {t.nav[k]}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <div className="hidden md:block"><LangSwitch dark={dark} /></div>
            <a href={SIGN_IN_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-signin-link" className={`hidden lg:inline-flex items-center gap-1 text-[13px] link-line transition-colors duration-500 ${dark ? "text-white/80 hover:text-white" : "text-[var(--ink-soft)] hover:text-[var(--ink)]"}`}>
              {t.nav.signIn}<ArrowUpRight className="h-3.5 w-3.5" />
            </a>
            <span className="hidden sm:block"><button onClick={() => go("contact")} data-testid="nav-cta-btn" className="btn-ink">{t.nav.cta}</button></span>
            <button onClick={() => setOpen(true)} data-testid="nav-menu-btn" aria-label={t.nav.menu} className="xl:hidden grid h-10 w-10 place-items-center rounded-full border border-[var(--line)] bg-white/80 text-[var(--ink)]">
              <Menu className="h-4 w-4" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            key="menu"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.7, ease }}
            className="fixed inset-0 z-[60] bg-white flex flex-col"
            data-testid="mobile-menu"
            data-lenis-prevent
          >
            <div className="flex h-[72px] items-center justify-between px-5">
              <Logo />
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label={t.nav.close} className="grid h-10 w-10 place-items-center rounded-full border border-[var(--line)]">
                <X className="h-4 w-4" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center gap-2 px-6">
              {LINKS.map((k, i) => (
                <motion.button
                  key={k}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 + i * 0.06, duration: 0.7, ease }}
                  onClick={() => go(k)}
                  data-testid={`mobile-link-${k}`}
                  className="text-start font-display text-5xl text-[var(--ink)] py-1"
                >
                  {t.nav[k]}
                </motion.button>
              ))}
            </nav>
            <div className="flex flex-wrap items-center justify-between gap-4 px-6 pb-10">
              <LangSwitch testPrefix="mobile" />
              <a href={SIGN_IN_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-signin-link" className="inline-flex items-center gap-1 text-sm text-[var(--ink)]">
                {t.nav.signIn}<ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
